import { GQL } from '@aline/graphql';
import { z } from 'zod';

/**
 * The launch form every surface renders: Slack modal, hosted page (email and Teams) and the
 * in-app approval card. The server builds one `LaunchFormSpec` from the launch tool input and
 * the conversation's files, and each renderer draws it with the copy below so the wording stays
 * the same everywhere.
 */

export const MAX_CONVERSATION_FILES = 20;
export const MAX_LAUNCH_DOCUMENT_CHOICES = 50;

/** Third-party paper must open as a contract, so only PDF and Word files can replace a document. */
export const REPLACEMENT_FILE_EXTENSIONS = ['pdf', 'doc', 'docx'] as const;

/** Submission errors for the extra documents are keyed by this id; it is also the Slack block id. */
export const LAUNCH_EXTRA_DOCUMENTS_BLOCK_ID = 'launch_extra_docs';

const CONVERSATION_FILE_SOURCES = [
  'colleague',
  'conversation',
  'drafted',
  'imported',
  'uploaded',
] as const;
const ConversationFileSourceSchema = z.enum(CONVERSATION_FILE_SOURCES);
export type ConversationFileSource = z.infer<typeof ConversationFileSourceSchema>;

const ConversationFileSchema = z.object({
  addedAt: z.string(),
  /** First name of whoever attached it, set for a colleague's file. */
  addedByName: z.string().nullable(),
  canReplace: z.boolean(),
  documentId: z.string(),
  /** Attached to the message that asked for the launch, so it goes in by default. */
  isRequestFile: z.boolean(),
  name: z.string(),
  source: ConversationFileSourceSchema,
});
export type ConversationFile = z.infer<typeof ConversationFileSchema>;

const LaunchFormFileSchema = z.object({
  documentId: z.string(),
  name: z.string(),
  sourcePhrase: z.string(),
});
export type LaunchFormFile = z.infer<typeof LaunchFormFileSchema>;

const LaunchFormTemplateDocumentSchema = z.object({
  documentRole: z.enum(GQL.WorkflowFileDocumentRole),
  name: z.string(),
  /** The file suggested in its place, or null to launch the template's copy. */
  replacement: LaunchFormFileSchema.nullable(),
  templateDocumentId: z.string(),
});
export type LaunchFormTemplateDocument = z.infer<typeof LaunchFormTemplateDocumentSchema>;

const LaunchFormExtraDocumentSchema = LaunchFormFileSchema.extend({
  documentRole: z.enum(GQL.WorkflowFileDocumentRole),
});
export type LaunchFormExtraDocument = z.infer<typeof LaunchFormExtraDocumentSchema>;

const LaunchFormSkippedFileSchema = z.object({ name: z.string(), reason: z.string() });

export const LaunchFormSpecSchema = z.object({
  /** Files the pickers offer: this conversation's files, newest first, request files included. */
  conversationFiles: z.array(ConversationFileSchema),
  excludedDocumentIds: z.array(z.string()),
  extraDocuments: z.array(LaunchFormExtraDocumentSchema),
  skippedFiles: z.array(LaunchFormSkippedFileSchema),
  templateDocuments: z.array(LaunchFormTemplateDocumentSchema),
  workflowName: z.string(),
});
export type LaunchFormSpec = z.infer<typeof LaunchFormSpecSchema>;

const LaunchDocumentReplacementSchema = z.object({
  documentId: z.string(),
  templateDocumentId: z.string(),
});
export type LaunchDocumentReplacement = z.infer<typeof LaunchDocumentReplacementSchema>;

const LaunchDocumentAttachmentSchema = z.object({
  documentId: z.string(),
  documentRole: z.enum(GQL.WorkflowFileDocumentRole),
});

/** What the user chose in the Documents section of any launch form. */
export const LaunchDocumentChoicesSchema = z.object({
  documentAttachments: z.array(LaunchDocumentAttachmentSchema).max(MAX_LAUNCH_DOCUMENT_CHOICES),
  documentReplacements: z.array(LaunchDocumentReplacementSchema).max(MAX_LAUNCH_DOCUMENT_CHOICES),
  excludedDocumentIds: z.array(z.string()).max(MAX_LAUNCH_DOCUMENT_CHOICES),
});
export type LaunchDocumentChoices = z.infer<typeof LaunchDocumentChoicesSchema>;

export const LAUNCH_FORM_COPY = {
  addDocument: 'Add document',
  addFromConversation: 'Add from this conversation',
  cancel: 'Cancel',
  documentsTitle: 'Documents',
  documentsUnavailable: 'The launch documents could not be loaded. Reopen the form.',
  dontInclude: "Don't include this document",
  fieldsTitle: 'Document fields',
  intakeTitle: 'Intake questions',
  launch: 'Launch workflow',
  notIncluded: 'Not included',
  orUpload: 'Or upload',
  remove: 'Remove',
  replace: 'Replace with third-party paper',
  replaceShort: 'Replace',
  restore: 'Restore',
  restoreOriginal: 'Restore original',
  reviewAndLaunch: 'Review and launch',
  rolesTitle: 'Assign roles',
  staleFile: 'This file is no longer available. Pick another or upload it again.',
  title: 'Launch details',
  uploadDifferent: 'Upload a different file…',
  workflowName: 'Workflow name',
  wrongFileType: 'Third-party paper must be a PDF or Word file.',
} as const;

const SOURCE_PHRASE: Record<ConversationFileSource, (addedByName: string | null) => string> = {
  colleague: addedByName =>
    addedByName ? `from ${addedByName}'s message` : 'from this conversation',
  conversation: () => 'from this conversation',
  drafted: () => 'drafted by Aline',
  imported: () => 'from your connected account',
  uploaded: () => 'uploaded just now',
};

/** Submission errors for one template document are keyed by this id; it is also its Slack row. */
export function getLaunchDocumentBlockId(templateDocumentId: string): string {
  return `launch_doc_${templateDocumentId}`;
}

export function getConversationFileSourcePhrase(
  file: Pick<ConversationFile, 'addedByName' | 'source'>
): string {
  return SOURCE_PHRASE[file.source](file.addedByName);
}

export function toLaunchFormFile(file: ConversationFile): LaunchFormFile {
  return {
    documentId: file.documentId,
    name: file.name,
    sourcePhrase: getConversationFileSourcePhrase(file),
  };
}

/** `Using "Acme NDA.pdf" from this conversation instead of your template "Mutual NDA"`. */
export function getReplacementLine(document: LaunchFormTemplateDocument): string | null {
  if (!document.replacement) return null;
  return `Using ${quoteName(document.replacement.name)} ${document.replacement.sourcePhrase} instead of your template ${quoteName(document.name)}`;
}

export function getSkippedFileLine(file: LaunchFormSpec['skippedFiles'][number]): string {
  return `Couldn't use ${quoteName(file.name)}: ${file.reason}`;
}

export function getIsReplacementFileName(name: string): boolean {
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  return REPLACEMENT_FILE_EXTENSIONS.some(allowed => allowed === extension);
}

/**
 * One line per file decision, for the approval card, the email body and the confirmation. Empty
 * when the launch uses only the template's own documents.
 */
export function getLaunchFileSummary(
  spec: Pick<
    LaunchFormSpec,
    'excludedDocumentIds' | 'extraDocuments' | 'skippedFiles' | 'templateDocuments'
  >,
  intakeFiles: readonly { fieldLabel: string; file: LaunchFormFile }[] = []
): string[] {
  const excluded = new Set(spec.excludedDocumentIds);
  const documentLines = spec.templateDocuments.flatMap(document => {
    if (excluded.has(document.templateDocumentId))
      return [`Not including ${quoteName(document.name)}`];
    const line = getReplacementLine(document);
    return line ? [line] : [];
  });
  return [
    ...documentLines,
    ...spec.extraDocuments.map(file => `Adding ${quoteName(file.name)} ${file.sourcePhrase}`),
    ...intakeFiles.map(
      ({ fieldLabel, file }) =>
        `Using ${quoteName(file.name)} ${file.sourcePhrase} for ${fieldLabel}`
    ),
    ...spec.skippedFiles.map(getSkippedFileLine),
  ];
}

export function getInitialLaunchDocumentChoices(spec: LaunchFormSpec): LaunchDocumentChoices {
  return {
    documentAttachments: spec.extraDocuments.map(({ documentId, documentRole }) => ({
      documentId,
      documentRole,
    })),
    documentReplacements: spec.templateDocuments.flatMap(document =>
      document.replacement
        ? [
            {
              documentId: document.replacement.documentId,
              templateDocumentId: document.templateDocumentId,
            },
          ]
        : []
    ),
    excludedDocumentIds: spec.excludedDocumentIds,
  };
}

/**
 * Only fields of documents the launch will copy. A cached template can still list fields of a
 * document deleted from the template elsewhere, and a document removed or replaced with
 * third-party paper never reaches the workflow, so its values would be rejected.
 */
export function getLaunchDocumentFields<TField extends { usedInDocuments: readonly string[] }>(
  {
    files,
    intakeFields,
  }: { files: readonly { document: { id: string } }[]; intakeFields: readonly TField[] },
  {
    excludedDocumentIds,
    replacementFiles,
  }: { excludedDocumentIds: readonly string[]; replacementFiles: readonly { templateId: string }[] }
): TField[] {
  const droppedIds = new Set([...excludedDocumentIds, ...replacementFiles.map(f => f.templateId)]);
  const launchedIds = new Set(files.map(f => f.document.id).filter(id => !droppedIds.has(id)));

  return intakeFields.flatMap(field => {
    const usedInDocuments = field.usedInDocuments.filter(id => launchedIds.has(id));
    return usedInDocuments.length ? [{ ...field, usedInDocuments }] : [];
  });
}

/** `getLaunchDocumentFields` for a launch form, reading the dropped documents from its choices. */
export function getLaunchFormDocumentFields<TField extends { usedInDocuments: readonly string[] }>({
  choices,
  intakeFields,
  templateDocumentIds,
}: {
  choices: Pick<LaunchDocumentChoices, 'documentReplacements' | 'excludedDocumentIds'>;
  intakeFields: readonly TField[];
  templateDocumentIds: readonly string[];
}): TField[] {
  return getLaunchDocumentFields(
    { files: templateDocumentIds.map(id => ({ document: { id } })), intakeFields },
    {
      excludedDocumentIds: choices.excludedDocumentIds,
      replacementFiles: choices.documentReplacements.map(({ templateDocumentId }) => ({
        templateId: templateDocumentId,
      })),
    }
  );
}

/**
 * The spec as the user left it: replacements and extra documents read from their choices instead
 * of the model's suggestions. `uploadedFiles` names the files uploaded on the form itself.
 */
export function applyLaunchDocumentChoices({
  choices,
  spec,
  uploadedFiles = [],
}: {
  choices: LaunchDocumentChoices;
  spec: LaunchFormSpec;
  uploadedFiles?: readonly Pick<LaunchFormFile, 'documentId' | 'name'>[];
}): LaunchFormSpec {
  const fileById = getLaunchFileDirectory(spec, uploadedFiles);
  const toFile = (documentId: string): LaunchFormFile =>
    fileById.get(documentId) ?? {
      documentId,
      name: 'Uploaded file',
      sourcePhrase: 'uploaded just now',
    };
  const replacementByTemplateId = new Map(
    choices.documentReplacements.map(replacement => [
      replacement.templateDocumentId,
      toFile(replacement.documentId),
    ])
  );
  return {
    ...spec,
    excludedDocumentIds: choices.excludedDocumentIds,
    extraDocuments: choices.documentAttachments.map(({ documentId, documentRole }) => ({
      ...toFile(documentId),
      documentRole,
    })),
    templateDocuments: spec.templateDocuments.map(document => ({
      ...document,
      replacement: replacementByTemplateId.get(document.templateDocumentId) ?? null,
    })),
  };
}

/**
 * Use a file in place of a template document. It stops being an extra document, and a document
 * it already replaced goes back to its template copy, since one file can replace only one.
 */
export function replaceLaunchTemplateDocument(
  choices: LaunchDocumentChoices,
  { documentId, templateDocumentId }: LaunchDocumentReplacement
): LaunchDocumentChoices {
  return {
    documentAttachments: choices.documentAttachments.filter(
      attachment => attachment.documentId !== documentId
    ),
    documentReplacements: [
      ...choices.documentReplacements.filter(
        replacement =>
          replacement.templateDocumentId !== templateDocumentId &&
          replacement.documentId !== documentId
      ),
      { documentId, templateDocumentId },
    ],
    excludedDocumentIds: choices.excludedDocumentIds.filter(id => id !== templateDocumentId),
  };
}

export function restoreLaunchTemplateDocument(
  choices: LaunchDocumentChoices,
  templateDocumentId: string
): LaunchDocumentChoices {
  return {
    ...choices,
    documentReplacements: choices.documentReplacements.filter(
      replacement => replacement.templateDocumentId !== templateDocumentId
    ),
    excludedDocumentIds: choices.excludedDocumentIds.filter(id => id !== templateDocumentId),
  };
}

export function excludeLaunchTemplateDocument(
  choices: LaunchDocumentChoices,
  templateDocumentId: string
): LaunchDocumentChoices {
  return {
    ...restoreLaunchTemplateDocument(choices, templateDocumentId),
    excludedDocumentIds: [
      ...choices.excludedDocumentIds.filter(id => id !== templateDocumentId),
      templateDocumentId,
    ],
  };
}

/** Add a file as a General document. A file already in the form is left where it is. */
export function addLaunchExtraDocument(
  choices: LaunchDocumentChoices,
  documentId: string
): LaunchDocumentChoices {
  const isUsed =
    choices.documentAttachments.some(attachment => attachment.documentId === documentId) ||
    choices.documentReplacements.some(replacement => replacement.documentId === documentId);
  if (isUsed) return choices;
  return {
    ...choices,
    documentAttachments: [
      ...choices.documentAttachments,
      { documentId, documentRole: GQL.WorkflowFileDocumentRole.General },
    ],
  };
}

export function removeLaunchExtraDocument(
  choices: LaunchDocumentChoices,
  documentId: string
): LaunchDocumentChoices {
  return {
    ...choices,
    documentAttachments: choices.documentAttachments.filter(
      attachment => attachment.documentId !== documentId
    ),
  };
}

/** Conversation files "Add document" offers: those not already in the form. */
export function getAddableLaunchFiles(
  spec: Pick<LaunchFormSpec, 'conversationFiles'>,
  choices: LaunchDocumentChoices
): ConversationFile[] {
  const used = new Set([
    ...choices.documentAttachments.map(attachment => attachment.documentId),
    ...choices.documentReplacements.map(replacement => replacement.documentId),
  ]);
  return spec.conversationFiles.filter(file => !used.has(file.documentId));
}

/** Maps the form's choices to the `replacementFiles` shape `createWorkflowInstance` takes. */
export function toReplacementFiles(
  replacements: readonly LaunchDocumentReplacement[]
): { replacementFileId: string; templateId: string }[] {
  return replacements.map(({ documentId, templateDocumentId }) => ({
    replacementFileId: documentId,
    templateId: templateDocumentId,
  }));
}

function getLaunchFileDirectory(
  spec: LaunchFormSpec,
  uploadedFiles: readonly Pick<LaunchFormFile, 'documentId' | 'name'>[]
): Map<string, LaunchFormFile> {
  const entries: LaunchFormFile[] = [
    ...uploadedFiles.map(file => ({ ...file, sourcePhrase: SOURCE_PHRASE.uploaded(null) })),
    ...spec.extraDocuments,
    ...spec.templateDocuments.flatMap(document =>
      document.replacement ? [document.replacement] : []
    ),
    ...spec.conversationFiles.map(toLaunchFormFile),
  ];
  return new Map(entries.map(file => [file.documentId, file]));
}

function quoteName(name: string): string {
  return `"${name}"`;
}
